import React, { useEffect, useRef, useState } from "react";
import { X, PartyPopper } from "lucide-react";

// Konfety — jednoduchá vanilla canvas implementace (bez externí knihovny).
// Rozhází barevné kroužky shora, padají se simulovanou gravitací + rotací.
function runConfetti(canvas) {
  const ctx = canvas.getContext("2d");
  const W = (canvas.width = window.innerWidth);
  const H = (canvas.height = window.innerHeight);
  const colors = ["#6E519F", "#10b981", "#f59e0b", "#3b82f6", "#ec4899", "#06b6d4", "#ef4444", "#22c55e", "#eab308"];
  const pieces = [];
  for (let i = 0; i < 180; i++) {
    pieces.push({
      x: Math.random() * W,
      y: -20 - Math.random() * 200,
      size: 6 + Math.random() * 8,
      color: colors[Math.floor(Math.random() * colors.length)],
      vx: (Math.random() - 0.5) * 4,
      vy: 2 + Math.random() * 4,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.2,
      shape: Math.random() < 0.5 ? "rect" : "circle",
    });
  }
  let running = true;
  let start = Date.now();
  function frame() {
    if (!running) return;
    ctx.clearRect(0, 0, W, H);
    pieces.forEach((p) => {
      p.vy += 0.08; // gravitace
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vr;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === "rect") {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    });
    // Ukončit po 6 sekundách nebo když všechny padnou dolů
    if (Date.now() - start < 6000) {
      requestAnimationFrame(frame);
    } else {
      running = false;
      ctx.clearRect(0, 0, W, H);
    }
  }
  frame();
  return () => { running = false; };
}

/**
 * CelebrationModal — zobrazí overlay s oslavou pro dosažený milník.
 * Props:
 *  - milestone: objekt { metricKey, cfg, threshold, currentValue }
 *  - onClose: callback po zavření
 */
export default function CelebrationModal({ milestone, onClose }) {
  const canvasRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!milestone) return;
    // Malé zpoždění pro fade-in
    const t = setTimeout(() => setVisible(true), 40);
    const stopConfetti = runConfetti(canvasRef.current);
    return () => {
      clearTimeout(t);
      stopConfetti && stopConfetti();
    };
  }, [milestone]);

  if (!milestone) return null;
  const { cfg, threshold, currentValue } = milestone;

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  return (
    <>
      {/* Canvas pro konfety — přes celou obrazovku, nad modalem */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-[60]"
      />
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        style={{ background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" }}
        onClick={handleClose}
      >
        {/* Modal content */}
        <div
          className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all duration-500 ${
            visible ? "scale-100 translate-y-0" : "scale-75 translate-y-8"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Barevný header s gradientem */}
          <div className={`bg-gradient-to-br ${cfg.color} px-6 py-10 text-center text-white relative`}>
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              aria-label="Zavřít"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-7xl mb-3 animate-bounce">{cfg.emoji}</div>
            <div className="text-sm uppercase tracking-wider opacity-90 mb-1 font-semibold">
              Nový milník!
            </div>
            <div className="text-3xl font-extrabold leading-tight">
              Gratulace!
            </div>
          </div>
          {/* Tělo */}
          <div className="p-6 text-center">
            <div className="text-sm text-slate-500 mb-2 font-medium uppercase tracking-wider">{cfg.label}</div>
            <div className="text-4xl font-extrabold text-slate-900 tabular-nums mb-3">
              {cfg.formatValue(threshold)}
            </div>
            <p className="text-base text-slate-700 mb-4 leading-relaxed">
              {cfg.message(threshold)}
            </p>
            {currentValue > threshold && (
              <div className="text-sm text-slate-500 mb-5">
                Aktuální stav: <span className="font-bold text-slate-800">{cfg.formatValue(currentValue)}</span>
              </div>
            )}
            <button
              onClick={handleClose}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold bg-gradient-to-r ${cfg.color} hover:opacity-90 shadow-md transition-opacity`}
            >
              <PartyPopper className="w-5 h-5" />
              Oslavit
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
